/**
 * Formats an array of day names into a compact, readable string
 * Examples:
 * - ["Monday"] → "Mon"
 * - ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"] → "Mon-Fri (5 days)"
 * - ["Monday", "Wednesday", "Friday"] → "Mon, Wed, Fri (3 days)"
 * - ["Monday", "Tuesday", "Wednesday"] → "Mon-Wed (3 days)"
 */
export function formatDayRange(days: string[]): string {
  if (!days || days.length === 0) return '';
  
  // Map full day names to abbreviations
  const dayAbbreviations: Record<string, string> = {
    'Monday': 'Mon',
    'Tuesday': 'Tue',
    'Wednesday': 'Wed',
    'Thursday': 'Thu',
    'Friday': 'Fri',
    'Saturday': 'Sat',
    'Sunday': 'Sun'
  };
  
  // Order of days in a week
  const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  
  // Sort days by their order in the week
  const sortedDays = [...days].sort((a, b) => {
    return dayOrder.indexOf(a) - dayOrder.indexOf(b);
  });
  
  // Single day
  if (sortedDays.length === 1) {
    return dayAbbreviations[sortedDays[0]] || sortedDays[0];
  }
  
  // Check if it's a consecutive range
  const isConsecutive = sortedDays.every((day, index) => {
    if (index === 0) return true;
    const prevDayIndex = dayOrder.indexOf(sortedDays[index - 1]);
    const currentDayIndex = dayOrder.indexOf(day);
    return currentDayIndex === prevDayIndex + 1;
  });
  
  if (isConsecutive) {
    // It's a range
    const firstDay = dayAbbreviations[sortedDays[0]] || sortedDays[0];
    const lastDay = dayAbbreviations[sortedDays[sortedDays.length - 1]] || sortedDays[sortedDays.length - 1];
    
    if (sortedDays.length === 2) {
      // Just two consecutive days, use comma
      return `${firstDay}, ${lastDay} (2 days)`;
    }
    
    return `${firstDay}-${lastDay} (${sortedDays.length} days)`;
  }
  
  // Non-consecutive days, list them
  const abbreviated = sortedDays.map(day => dayAbbreviations[day] || day);
  
  if (sortedDays.length === 2) {
    return `${abbreviated[0]}, ${abbreviated[1]} (2 days)`;
  }
  
  return `${abbreviated.join(', ')} (${sortedDays.length} days)`;
}

/**
 * Calculates total hours from hours per day and number of days
 */
export function calculateTotalHours(hoursPerDay: number, numberOfDays: number): number {
  return hoursPerDay * numberOfDays;
}

/**
 * Formats hours for display (removes unnecessary decimals)
 */
export function formatHours(hours: number): string {
  if (hours % 1 === 0) {
    return `${hours}h`;
  }
  return `${hours.toFixed(1)}h`;
}
